import React from 'react'
import { styled, theme } from '../../../../stitches.config'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight } from '@fortawesome/free-solid-svg-icons/faArrowRight'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    label: string
    variant?: 'primary' | 'secondary' | 'primaryOutline' | 'secondaryOutline'
    size?: 'sm' | 'md' | 'lg'
    width?: 'standard' | 'fitContent' | 'full'
    textTransform?: 'uppercase' | 'lowercase' | 'none'
    onClick?: () => void
    testId?: string
    css?: object
    builderBlock?: any
    builderState?: any
    showArrow?: boolean
    colorScheme?: string
}

const sharedButtonStyles = {
    color: '$white',
    transition: 'background-color 150ms ease',
    border: '2px solid transparent',
}

export const StyledButton = styled('button', {
    width: 'fit-content',
    maxWidth: '100%',
    fontWeight: 500,
    fontFamily: theme.fonts.default,
    letterSpacing: '0.05em',
    cursor: 'pointer',
    lineHeight: '1.25',
    '&:disabled': {
        background: '$mediumGray',
        borderColor: '$mediumGray',
        cursor: 'not-allowed',
        '&:hover': {
            background: '$light',
        },
    },
    variants: {
        variant: {
            primary: {
                backgroundColor: '$primary',
                ...sharedButtonStyles,
                '&:not(:disabled):hover': {
                    borderColor: '$primary',
                    backgroundColor: '$primaryLight',
                    color: '$primary',
                },
            },
            primaryOutline: {
                color: '$primary',
                backgroundColor: '$white',
                transition: 'background-color 150ms ease',
                border: '2px solid $primary',
                '&:not(:disabled):hover': {
                    borderColor: '$primary',
                    backgroundColor: '$primaryLight',
                },
            },
            secondary: {
                backgroundColor: '$secondary',
                ...sharedButtonStyles,
                '&:not(:disabled):hover': {
                    borderColor: '$secondary',
                    backgroundColor: '$secondaryLight',
                    color: '$secondary',
                },
            },
            secondaryOutline: {
                color: '$secondary',
                backgroundColor: '$white',
                transition: 'background-color 150ms ease',
                border: '2px solid $secondary',
                '&:not(:disabled):hover': {
                    borderColor: '$secondary',
                    backgroundColor: '$secondaryLight',
                },
            },
            deactivated: {
                color: '$white',
                backgroundColor: '$mediumGray',
                transition: 'background-color 150ms ease',
                border: '2px solid $mediumGray',
            },
        },
        size: {
            sm: {
                borderRadius: '48px',
                px: '$4',
                py: '$2',
                fontSize: '$3',
            },
            md: {
                borderRadius: '48px',
                px: '$3',
                py: '$2',
                fontSize: '$5',
            },
            lg: {
                borderRadius: '48px',
                px: '$4',
                py: '$2',
                fontSize: '$7',
            },
        },
        width: {
            standard: {
                minWidth: '275px',
            },
            fitContent: {
                width: 'fit-content',
            },
            full: {
                width: '100%',
            },
        },
    },
    defaultVariants: {
        variant: 'primary',
        size: 'md',
        width: 'fitContent',
    },
})

const Arrow = styled('span', {
    marginLeft: '.3em',
})

export const Button = ({
    variant,
    size = 'md',
    textTransform,
    label,
    showArrow = false,
    builderState,
    builderBlock,
    colorScheme, // @TODO remove - I think we need to remove it from all usages in Builder first?
    ...props
}: ButtonProps) => {
    const { css: cssProp, ...propsWithoutCss } = props
    const combinedCss = {
        textTransform: textTransform ?? 'none',
        ...cssProp,
    }

    return (
        <StyledButton
            variant={variant}
            size={size}
            css={combinedCss}
            {...propsWithoutCss}
        >
            {label}
            {props.children}
            {showArrow && (
                <FontAwesomeIcon
                    icon={faArrowRight}
                    style={{ marginLeft: '0.5rem' }}
                />
            )}
        </StyledButton>
    )
}

export default Button
